import type {
  ClaimedRange,
  LadderNow,
  RangeLadder,
  RangeRung,
} from "@patent-studio/api-types";
import { CAPTION_LINE } from "@/components/ui/marks";
import { Note } from "@/components/ui/Note";
import { ChildLines, type ChildLine } from "./ChildLines";
import type { LadderKey } from "./framework-edits";

/** What a claim row's ladders send, one rung at a time, each under the ladder it belongs to. */
export interface LadderActions {
  accept: (ladder: LadderKey, itemId: string, accepted: boolean) => void;
  reword: (
    ladder: LadderKey,
    itemId: string,
    key: string,
    putBack: () => void,
  ) => void;
  add: (ladder: LadderKey, key: string, putBack: () => void) => void;
  drop: (ladder: LadderKey, itemId: string) => void;
}

/** What the ladders need named that the framework holds only by id. */
export interface LadderNames {
  claim: (claimId: string) => number | undefined;
  recital: (recitalId: string) => string | undefined;
}

function sameRange(one: ClaimedRange | null, two: ClaimedRange): boolean {
  return (
    one !== null &&
    one.low === two.low &&
    one.high === two.high &&
    one.unit === two.unit &&
    one.about === two.about &&
    one.by_weight === two.by_weight
  );
}

/** A rung as its line draws it: its box, its sources, and the one note that applies. */
function rungLine(
  rung: RangeRung,
  outside: boolean,
  claimNumber: string,
): ChildLine {
  const sources = rung.sources ?? [];
  return {
    item_id: rung.item_id,
    key: rung.key,
    origin: rung.origin,
    accepted: rung.accepted,
    source:
      sources.length === 0
        ? undefined
        : { label: sources.map((source) => source.label).join(" · ") },
    note: outside
      ? { words: `outside ${claimNumber}'s range`, tone: "warning" }
      : !rung.basis_stated
        ? { words: "basis not stated", tone: "warning" }
        : undefined,
  };
}

/** Why a claimed range has no rung: the amounts the examples give it that none could use. */
function noFallback(ladder: RangeLadder): string {
  const asideOf = (reason: "unit" | "outside") =>
    (ladder.set_aside ?? [])
      .filter((aside) => aside.reason === reason)
      .map((aside) => aside.words);
  const joined = (words: string[]) =>
    words.length < 2
      ? words.join("")
      : `${words.slice(0, -1).join(", ")} and ${words.at(-1) ?? ""}`;
  const unit = asideOf("unit");
  const outside = asideOf("outside");
  if (unit.length > 0)
    return `No supported fallback: the examples give ${joined(unit)}, which are never turned into ${ladder.claimed.unit}.`;
  if (outside.length > 0)
    return `No supported fallback: the examples give ${joined(outside)}, outside the claimed range.`;
  return "No supported fallback: no worked example states an amount for it.";
}

function Ladder({
  ladder,
  now,
  names,
  readOnly,
  actions,
}: {
  ladder: RangeLadder;
  now: LadderNow | undefined;
  names: LadderNames;
  readOnly: boolean;
  actions?: LadderActions;
}) {
  const { claimed } = ladder;
  const key = { claimId: claimed.claim_id, recitalId: claimed.recital_id };
  const number = names.claim(claimed.claim_id);
  const claim = number === undefined ? "the claim" : `claim ${String(number)}`;
  // Once the claim's range changes, Praxis's rungs drop; nothing here blocks a confirmation.
  const changed = now !== undefined && !sameRange(now.claimed, claimed);
  const outside = new Set(now?.outside ?? []);
  const rungs = (ladder.rungs ?? []).filter(
    (rung) => !changed || rung.origin === "attorney",
  );
  const head = [
    number === undefined ? undefined : `Claim ${String(number)}`,
    names.recital(claimed.recital_id),
    (changed ? now.claimed?.words : undefined) ?? claimed.words,
  ].filter((part) => part !== undefined);
  return (
    <div className="space-y-1">
      <p className={CAPTION_LINE}>{head.join(" · ")}</p>
      {rungs.length === 0 && !changed && <Note>{noFallback(ladder)}</Note>}
      <ChildLines
        items={rungs.map((rung) =>
          rungLine(rung, outside.has(rung.item_id), claim),
        )}
        label={`Narrower ranges for ${claim}`}
        addLabel="Add range"
        placeholders={{ key: "A narrower range, e.g. 40 to 75 percent" }}
        keyOnly
        readOnly={readOnly || actions === undefined}
        onAdd={({ key: words }, putBack) => {
          actions?.add(key, words, putBack);
        }}
        onReword={(itemId, { key: words }, putBack) => {
          actions?.reword(key, itemId, words, putBack);
        }}
        onDrop={(itemId) => {
          actions?.drop(key, itemId);
        }}
        onAccept={(itemId, accepted) => {
          actions?.accept(key, itemId, accepted);
        }}
      />
    </div>
  );
}

/**
 * The narrower ranges under a claim row: one group for each range a claim beneath it states,
 * headed by the claim, the thing it recites and the range, then its rungs as child lines. A
 * suggestion is stated only once its box is ticked; one with no stated weight basis says so.
 */
export function RangeLadders({
  ladders,
  now,
  names,
  readOnly,
  actions,
}: {
  ladders: RangeLadder[];
  /** Each ladder read against the claims as they stand, from the framework read. */
  now: LadderNow[];
  names: LadderNames;
  readOnly: boolean;
  actions?: LadderActions;
}) {
  return (
    <div className="space-y-3 pl-11">
      {ladders.map((ladder) => (
        <Ladder
          key={`${ladder.claimed.claim_id}:${ladder.claimed.recital_id}`}
          ladder={ladder}
          now={now.find(
            (read) =>
              read.claim_id === ladder.claimed.claim_id &&
              read.recital_id === ladder.claimed.recital_id,
          )}
          names={names}
          readOnly={readOnly}
          actions={actions}
        />
      ))}
    </div>
  );
}
